// ─── eBay Service ─────────────────────────────────────────────────

const EBAY_API_URL = "/api/ebay-search";

// Junk filter
const JUNK_PATTERNS = [
  /\bcode\b/i, /\bonline\b/i, /\bdigital\b/i, /\bptcgo\b/i, /\bptcgl\b/i,
  /\btcg live\b/i, /\blot of\s+\d/i, /\bbulk\b/i, /\bmystery\b/i,
  /\brepack\b/i, /\brandom\b/i, /\bbinder\b/i, /\bplaymat\b/i,
  /\bsleeve/i, /\bdeck box\b/i, /\bbooster box\b/i, /\bbooster pack\b/i,
  /\belite trainer\b/i, /\betb\b/i, /\bblister\b/i,
  /\bsealed\b/i, /\bdisplay\b/i,
  /\bcustom\b/i, /\bproxy\b/i, /\bfake\b/i, /\breplica\b/i,
  /\btoken\b/i, /\benergy card\b/i,
];

function isJunk(title) {
  return JUNK_PATTERNS.some((p) => p.test(title));
}

/**
 * Detect if a card is a Special Parallel (SP) variant
 */
function isSpecialParallel(card) {
  const name = (card.name || "").toLowerCase();
  const variant = (card.variant || "").toLowerCase();
  return name.includes("(sp)") || name.includes("(parallel)") ||
    variant.includes("parallel") || variant.includes("alternate") ||
    variant.includes("alt art") || variant.includes("special");
}

/**
 * Check if eBay listing matches the SP/normal variant we want
 */
function matchesVariant(title, wantSP) {
  const lower = title.toLowerCase();
  const listingIsSP = lower.includes(" sp ") || lower.includes("(sp)") ||
    lower.includes("alt art") || lower.includes("alternate art") ||
    lower.includes("parallel") || lower.includes("special art");

  // If we want SP, prefer listings with SP markers
  // If we want normal, prefer listings WITHOUT SP markers
  if (wantSP) return listingIsSP;
  return !listingIsSP;
}

/**
 * Score a listing for relevance to the card we're looking for
 */
function scoreListing(item, card, wantSP) {
  let score = 0;
  const title = (item.title || "").toLowerCase();
  const cardNumber = (card.card_number || "").toLowerCase();
  const cardName = (card.name || "").replace(/\s*\(SP\)\s*/gi, "").replace(/\s*\(Parallel\)\s*/gi, "").toLowerCase();

  // Card number match (most important)
  if (cardNumber && title.includes(cardNumber)) score += 50;

  // Name match
  if (cardName && title.includes(cardName)) score += 30;

  // Variant match
  if (matchesVariant(title, wantSP)) score += 20;
  else score -= 15; // Wrong variant penalty

  // Graded cards are less useful for price comparison
  if (/\bpsa\b|\bbgs\b|\bcgc\b|\bgraded\b/i.test(title)) score -= 25;

  // Chinese/Japanese version when we likely want English
  if (/\bchinese\b|\bjapanese\b|\bjpn\b/i.test(title)) score -= 10;

  return score;
}

/**
 * Extract set code from card number
 */
function extractSetCode(card) {
  const number = card.card_number || "";
  const match = number.match(/^([A-Z]{1,4}\d{1,3})/i);
  return match ? match[1] : "";
}

/**
 * Build search queries
 */
function buildQueries(card) {
  const queries = [];
  const name = (card.name || "").replace(/\s*\(SP\)\s*/g, "").replace(/\s*\(Parallel\)\s*/g, "").trim();
  const number = card.card_number || "";
  const setName = card.set_name || "";
  const game = card.game || "";
  const setCode = extractSetCode(card);
  const isSP = isSpecialParallel(card);

  if (game === "one_piece") {
    // For SP: include SP in first query
    if (number && isSP) {
      queries.push(`One Piece ${name} SP ${number}`);
      queries.push(`One Piece ${name} alt art ${number}`);
    }
    if (number && !isSP) {
      // For normal: just name + number (SP listings will be filtered by score)
      queries.push(`One Piece ${name} ${number}`);
    }
    if (number) {
      queries.push(`One Piece ${name} ${number}`);
    }
    if (setCode) {
      queries.push(`One Piece ${name} ${setCode}`);
    }
    queries.push(`One Piece TCG ${name}`);
  } else {
    if (number && setName) {
      queries.push(`Pokemon ${name} ${number} ${setName}`);
    }
    if (number) {
      queries.push(`Pokemon ${name} ${number}`);
    }
    if (setName) {
      queries.push(`Pokemon ${name} ${setName}`);
    }
    queries.push(`Pokemon ${name} card`);
  }

  // Deduplicate
  return [...new Set(queries)];
}

/**
 * Fetch from eBay API
 */
async function fetchEbay(query, limit = 25) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 12000);
  try {
    const url = `${EBAY_API_URL}?q=${encodeURIComponent(query)}&limit=${limit}&_t=${Date.now()}`;
    const response = await fetch(url, { signal: controller.signal, headers: { "Accept": "application/json" } });
    clearTimeout(timer);
    const ct = response.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return null;
    if (!response.ok) return null;
    return response.json();
  } catch { return null; }
}

/**
 * Search eBay — tries queries, scores and ranks results
 */
export async function searchEbayListings(card, limit = 10) {
  const queries = buildQueries(card);
  const wantSP = isSpecialParallel(card);
  let bestItems = [];

  for (const query of queries) {
    const result = await fetchEbay(query, 30);
    if (!result || !result.items) continue;

    // Filter junk, score, and sort
    const scored = result.items
      .filter((item) => !isJunk(item.title))
      .map((item) => ({ ...item, _score: scoreListing(item, card, wantSP) }))
      .sort((a, b) => b._score - a._score);

    const relevant = scored.filter((i) => i._score >= 20);

    if (relevant.length >= 3) {
      bestItems = relevant.slice(0, limit);
      return { total: result.total || relevant.length, items: bestItems, query };
    }

    // Keep best so far
    if (scored.length > bestItems.length) {
      bestItems = scored.slice(0, limit);
    }
  }

  if (bestItems.length > 0) {
    return { total: bestItems.length, items: bestItems.slice(0, limit), query: queries[0] };
  }

  return { total: 0, items: [], error: "No results found on eBay for this card." };
}

/**
 * Get eBay price summary — returns avg/low/high from relevant listings
 */
export async function getEbayPriceSummary(card) {
  const result = await searchEbayListings(card, 15);

  if (result.error) {
    return { avg: null, low: null, high: null, count: 0, listings: [], error: result.error };
  }

  if (!result.items || result.items.length === 0) {
    return { avg: null, low: null, high: null, count: 0, listings: [] };
  }

  // Use only non-graded, priced items for price calculation
  const priced = result.items.filter((i) =>
    i.price && i.price > 0 &&
    !/\bpsa\b|\bbgs\b|\bcgc\b|\bgraded\b/i.test(i.title)
  );

  if (priced.length === 0) {
    // Fall back to all priced items including graded
    const allPriced = result.items.filter((i) => i.price && i.price > 0);
    if (allPriced.length === 0) {
      return { avg: null, low: null, high: null, count: 0, listings: result.items.slice(0, 8) };
    }
    const prices = allPriced.map((i) => i.price);
    const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
    return {
      avg: Math.round(avg * 100) / 100,
      low: Math.min(...prices),
      high: Math.max(...prices),
      count: result.total || allPriced.length,
      listings: result.items.slice(0, 8),
    };
  }

  // Remove extreme outliers
  const sorted = [...priced].sort((a, b) => a.price - b.price);
  const median = sorted[Math.floor(sorted.length / 2)].price;
  const reasonable = priced.filter((i) => i.price >= median * 0.1 && i.price <= median * 10);
  const final = reasonable.length >= 2 ? reasonable : priced;

  const prices = final.map((i) => i.price);
  const avg = prices.reduce((a, b) => a + b, 0) / prices.length;

  return {
    avg: Math.round(avg * 100) / 100,
    low: Math.min(...prices),
    high: Math.max(...prices),
    count: result.total || priced.length,
    listings: result.items.slice(0, 8),
  };
}

/**
 * Bulk fetch eBay prices for an entire set of cards
 * Uses 1-2 broad queries per set (50 items each) and maps prices to all cards
 * 200 cards from 1 set = ~2-4 requests instead of 200
 */
export async function fetchEbayBulkPrices(cards) {
  if (!cards || cards.length === 0) return {};

  // Group cards by set
  const setGroups = {};
  cards.forEach((card) => {
    const key = `${card.game}|${card.set_code || card.set_name}`;
    if (!setGroups[key]) setGroups[key] = { game: card.game, setCode: card.set_code, setName: card.set_name, cards: [] };
    setGroups[key].cards.push(card);
  });

  const priceMap = {};

  for (const group of Object.values(setGroups)) {
    const prefix = group.game === "one_piece" ? "One Piece" : "Pokemon";
    const setId = group.setCode || "";
    const setName = group.setName || "";

    // Build 2-3 different queries to maximize coverage
    const queries = [];
    if (setId) queries.push(`${prefix} ${setId}`);
    if (setName && setName !== setId) queries.push(`${prefix} ${setName}`);
    if (queries.length === 0) queries.push(`${prefix} ${setName || setId} card`);

    // Fetch all listings across queries (50 per request)
    const allItems = [];
    const seenIds = new Set();
    for (const q of queries) {
      for (let offset = 0; offset < 150; offset += 50) {
        const result = await fetchEbay(q, 50);
        if (!result || !result.items || result.items.length === 0) break;
        result.items.forEach((item) => {
          if (!seenIds.has(item.id) && !isJunk(item.title)) {
            seenIds.add(item.id);
            allItems.push(item);
          }
        });
        if (result.items.length < 50) break; // No more pages
      }
    }

    if (allItems.length === 0) {
      group.cards.forEach((c) => { priceMap[c.id] = 0; });
      continue;
    }

    // Match listings to cards
    for (const card of group.cards) {
      const cardNum = (card.card_number || "").toLowerCase();
      const cardName = (card.name || "").replace(/\s*\(SP\)\s*/gi, "").replace(/\s*\(Parallel\)\s*/gi, "").toLowerCase();
      const wantSP = isSpecialParallel(card);

      // Match by card number (most precise)
      const numMatches = cardNum ? allItems.filter((item) => {
        const title = (item.title || "").toLowerCase();
        if (!title.includes(cardNum)) return false;
        const isSP = /\bsp\b|\balt art\b|\balternate art\b|\bparallel\b/.test(title);
        if (wantSP !== isSP) return false;
        if (/\bpsa\b|\bbgs\b|\bcgc\b|\bgraded\b/.test(title)) return false;
        return item.price > 0;
      }) : [];

      if (numMatches.length > 0) {
        const prices = numMatches.map((m) => m.price);
        priceMap[card.id] = Math.round((prices.reduce((a, b) => a + b, 0) / prices.length) * 100) / 100;
        continue;
      }

      // Fallback: match by name
      const nameMatches = cardName ? allItems.filter((item) => {
        const title = (item.title || "").toLowerCase();
        return title.includes(cardName) && item.price > 0 &&
          !/\bpsa\b|\bbgs\b|\bcgc\b|\bgraded\b/.test(title);
      }) : [];

      if (nameMatches.length > 0) {
        const prices = nameMatches.map((m) => m.price);
        priceMap[card.id] = Math.round((prices.reduce((a, b) => a + b, 0) / prices.length) * 100) / 100;
      } else {
        priceMap[card.id] = 0;
      }
    }
  }

  return priceMap;
}
